import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw from 'twin.macro';
import { atom } from 'jotai';

import { Spinner, Title } from '@restworld/ui-ds';

import { EmployerPrimaryLayoutHeader } from '../../layouts';
import { EmployerPageContentWrapper } from '../employer-page-content-wrapper';
import { useVerifyEmailFromURLTokenMutation } from './use-employer-user-information';
import { EditUserCommunicationPrefModal } from './edit-user-communication-pref';
import { ManageNotificationsModal } from './manage-notifications-modal';
import { EditUserInformationModal } from './edit-user-information-modal';
import { UserDeleteAction } from './user-delete-action';
import { ContactInformationSection } from './contact-information-section';
import { PersonalInformationSection } from './personal-information-section';
import UpdateUserEmailModal from './update-user-email-modal';
import UpdatePhoneNumberModal from './update-phone-number-modal';
import OTPValidationModal from './otp-validation-modal';
import { LanguageChangeModal } from './language-change-modal';

export const modalUserInformationVisibleAtom = atom<boolean>(false);
export const modalCommunicationPrefVisibleAtom = atom<boolean>(false);
export const isLanguageChangeModalVisibleAtom = atom<boolean>(false);
export const modalManageNotificationsVisibleAtom = atom<boolean>(false);
export type ModalEditKey =
  | 'name_and_surname'
  | 'communication_pref'
  | 'email'
  | 'phone_number'
  | 'picture'
  | 'organization'
  | 'account_permission_level'
  | 'language'
  | 'manage_notifications'
  | '';

export const modalEditKey = atom<ModalEditKey>('');

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IUserSettingsComponentProps {}
const UserSettingsComponent: React.FunctionComponent<IUserSettingsComponentProps> = (props) => {
  const { t } = useTranslation('user-settings');

  const { isPending: isEmailTokenVerificationPending } = useVerifyEmailFromURLTokenMutation();
  return (
    <>
      {isEmailTokenVerificationPending && (
        <Spinner withBackDrop size="lg" color="primary" tw="flex flex-col items-center justify-center gap-2">
          <Title
            fontSize="large"
            isCenter
            textColor="neutral-700"
            fontWeight="semibold"
            content={t('email_verification')}
            twStyle={tw`leading-7.5`}
          />
        </Spinner>
      )}
      <EmployerPageContentWrapper tw="py-[63px] xl:py-[80px]">
        <div tw="flex flex-col gap-[72px]">
          <EmployerPrimaryLayoutHeader pageTitle={t('user-settings-title')} showInfoIcon={false} />
          <PersonalInformationSection />
          <ContactInformationSection />
          <UserDeleteAction />
        </div>
      </EmployerPageContentWrapper>
      <EditUserInformationModal />
      <EditUserCommunicationPrefModal />
      {/* All employer notification settings */}
      <ManageNotificationsModal />
      <UpdateUserEmailModal />
      <UpdatePhoneNumberModal />
      <OTPValidationModal />
      <LanguageChangeModal />
    </>
  );
};

export default UserSettingsComponent;

export const ComponentLayout = ({ title, children }: { title?: string; children?: React.ReactNode }) => (
  <Container>
    <Title fontSize="large" fontWeight="semibold" content={title} />
    {children}
  </Container>
);
const Container = tw.div`flex flex-col gap-4`;
